import moment from 'moment';
import './EventRenderer.css';
import {
  VideoCameraOutlined,
  GlobalOutlined,
  HomeOutlined,
  QuestionOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const EventRenderer = ({ eventInfo }) => {
  const { t } = useTranslation();
  const isClass = eventInfo.event.id.startsWith('class-');
  const isTask = eventInfo.event.id.startsWith('task-');
  const isShared = eventInfo.event.extendedProps?.shared;
  
  const getEventTypeClass = () => {
    if (isClass) return 'event-class';
    if (isShared) return 'event-shared';
    return 'event-task';
  };

  // Helper function to format schedule hour from "00h 00m" to "HH:MM"
  const formatScheduleHour = (scheduleHour) => {
    if (!scheduleHour) return null;
    
    // Handle different possible formats
    const str = scheduleHour.toString();
    
    // If it's already in HH:MM format, return as is
    if (/^\d{1,2}:\d{2}$/.test(str)) {
      return str;
    }
    
    // Parse "00h 00m" format
    const hourMatch = str.match(/(\d+)h/);
    const minuteMatch = str.match(/(\d+)m/);
    
    const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
    const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;
    
    // Format as HH:MM
    return `${hours.toString().padStart(2, '0')}.${minutes.toString().padStart(2, '0')} hr`;
  };

  const renderTimeRange = () => (
    <div className="event-time">
      {moment(eventInfo.event.start).format('HH:mm')} - {moment(eventInfo.event.end).format('HH:mm')}
    </div>
  );

  // Month view rendering (compact)
  const renderMonthView = () => (
    <div 
      className={`event-content event-month ${getEventTypeClass()}`}
      style={{ 
        // CRITICAL: Allow FullCalendar to handle drag events
        pointerEvents: 'none',
        height: '100%',
        width: '100%'
      }}
    >
      {renderTimeRange()}
      <div className="event-body">
        {isClass ? renderClassInfo(false) : renderTaskInfo(false)}
      </div>
    </div>
  );

  // Week/Day view rendering (detailed)
  const renderDetailedView = () => (
    <div 
      className={`event-content event-detailed ${getEventTypeClass()}`}
      style={{ 
        // CRITICAL: Allow FullCalendar to handle drag events
        pointerEvents: 'none',
        height: '100%',
        width: '100%'
      }}
    >
      {renderTimeRange()}
      <div className="event-body">
        {isClass ? renderClassInfo(false) : renderTaskInfo(false)}
      </div>
    </div>
  );

  const renderClassInfo = (isCompact) => {
    const props = eventInfo.event.extendedProps;
    
    // The data is nested in extendedProps.extendedProps, so we need to access it properly
    const nestedProps = props?.extendedProps || props;
    
    // Try multiple sources for class data
    const classCode = nestedProps?.classCode || nestedProps?.class_code || eventInfo.event.title?.split(' - ')[0] || 'Class';
    const subjectName = nestedProps?.subjectName || nestedProps?.subject_name || eventInfo.event.title?.split(' - ')[1] || '';
    const classroomNumber = nestedProps?.classroomNumber || nestedProps?.classroom_number;
    const platform = nestedProps?.platform;
    const teacherName = nestedProps?.teacherName || nestedProps?.teacher_name;
    const scheduleHour = nestedProps?.scheduleHour || nestedProps?.schedule_hour;
    
    if (isCompact) {
      return (
        <div className="class-info-compact">
          <div>{classCode}</div>
          {subjectName && <div className="class-subject">{subjectName}</div>}
          {platform && platform !== 'null' && platform !== '' && (
            <div className="class-platform-compact">💻 {platform}</div>
          )}
        </div>
      );
    }

    const getPlatformIcon = (platform) => {
      switch (platform) {
        case 'online':
          return <VideoCameraOutlined style={{ marginRight: 4 }} />;
        case 'onsite':
          return <GlobalOutlined style={{ marginRight: 4 }} />;
        case 'athome':
          return <HomeOutlined style={{ marginRight: 4 }} />;
        default:
          return <QuestionOutlined style={{ marginRight: 4 }} />;
      }
    };

    const formattedScheduleHour = formatScheduleHour(scheduleHour);

    return (
      <div className="class-info-detailed">
        <div className="class-header">
          <div className="class-subject-large">{classCode}</div>
        </div>
         {subjectName && (
          <div className="class-subject-large">{subjectName}</div>
        )}
        <div className="class-details">
          {platform && (
            <span className={`platform-tag platform-${platform}`}>
              {getPlatformIcon(platform)} {t(platform)}
            </span>
          )}
          {formattedScheduleHour && (
            <div className="class-duration">
              ⏱️ {formattedScheduleHour}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTaskInfo = (isCompact) => {
    const props = eventInfo.event.extendedProps;
    const duration = props?.schedule_hour;
    
    if (isCompact) {
      return (
        <div className="task-info-compact">
          <div className="task-title">{eventInfo.event.title}</div>
          {isShared && <div className="shared-indicator">👥 Shared</div>}
        </div>
      );
    }

    return (
      <div className="task-info-detailed">
        <div className="task-header">
          <div className="task-title-large">{eventInfo.event.title}</div>
          {isShared && <div className="shared-badge">👥 Shared Task</div>}
        </div>
        
        {props?.description && (
          <div className="task-description">{props.description}</div>
        )}
      </div>
    );
  };

  // Determine which view to render based on calendar view type
  const isMonthView = eventInfo.view.type === 'dayGridMonth';
  
  return isMonthView ? renderMonthView() : renderDetailedView();
};

export default EventRenderer;